/**
 * HTML Preprocessor for Edit Mode
 * Adds unique data-edit-id attributes to all elements for tracking during visual editing
 */

/**
 * Generate a unique edit ID
 */
function generateEditId(): string {
  return `edit-${crypto.randomUUID()}`;
}

/**
 * Preprocess HTML for edit mode by adding unique data-edit-id to all elements
 * @param html - Source HTML string
 * @returns HTML with data-edit-id attributes added to all elements
 */
export function preprocessForEdit(html: string): string {
  try {
    console.log('🔧 [PREPROCESS] BEFORE parsing:');
    console.log('  - Length:', html.length, 'chars');
    console.log('  - Lines:', html.split('\n').length);
    console.log('  - First 300 chars:', html.substring(0, 300).replace(/\n/g, '\\n'));
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const generatedIds: string[] = [];
    
    // Walk through all elements in the body and add edit IDs
    const walker = document.createTreeWalker(
      doc.body,
      NodeFilter.SHOW_ELEMENT,
      null
    );
    
    let node: Node | null;
    while ((node = walker.nextNode())) {
      if (node instanceof Element) {
        // Add unique edit ID to each element
        const editId = generateEditId();
        node.setAttribute('data-edit-id', editId);
        generatedIds.push(editId);
      }
    }
    
    console.log('🔧 [PREPROCESS] Generated', generatedIds.length, 'edit IDs');
    
    // Return the full HTML document
    const result = doc.documentElement.outerHTML;
    
    console.log('🔧 [PREPROCESS] AFTER serialization (outerHTML):');
    console.log('  - Length:', result.length, 'chars (was', html.length, ') - CHANGED BY', result.length - html.length, 'chars');
    console.log('  - Lines:', result.split('\n').length, '(was', html.split('\n').length, ') - CHANGED BY', result.split('\n').length - html.split('\n').length, 'lines');
    console.log('  - First 300 chars:', result.substring(0, 300).replace(/\n/g, '\\n'));
    
    // Show a specific example of formatting change
    const origLines = html.split('\n');
    const resultLines = result.split('\n');
    console.log('🔧 [PREPROCESS] EXAMPLE - Lines 20-25 comparison:');
    for (let i = 19; i < 25; i++) {
      const origLine = origLines[i] || '(missing)';
      const resultLine = resultLines[i] || '(missing)';
      if (origLine.trim() !== resultLine.trim()) {
        console.log(`  Line ${i + 1} CHANGED:`);
        console.log(`    BEFORE: "${origLine}"`);
        console.log(`    AFTER:  "${resultLine}"`);
      }
    }
    
    return result;
  } catch (error) {
    console.error('Error preprocessing HTML for edit mode:', error);
    return html; // Return original if parsing fails
  }
}

/**
 * Strip all edit-mode attributes and injected scripts from HTML
 * @param html - HTML with data-edit-* attributes and edit mode script
 * @returns Clean HTML without edit-mode attributes or scripts
 */
export function stripEditAttributes(html: string): string {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Find all elements with data-edit-* attributes
    const elementsWithEditAttrs = doc.querySelectorAll('[data-edit-id], [data-edit-selected]');
    
    console.log('🧹 [STRIP] Removing edit attributes from', elementsWithEditAttrs.length, 'elements');
    
    elementsWithEditAttrs.forEach(element => {
      // Remove all attributes starting with "data-edit-"
      Array.from(element.attributes).forEach(attr => {
        if (attr.name.startsWith('data-edit-')) {
          element.removeAttribute(attr.name);
        }
      });
    });
    
    // Remove injected edit mode script(s)
    const scripts = doc.querySelectorAll('script');
    scripts.forEach(script => {
      // Remove scripts that contain edit mode code (check for signature)
      if (script.textContent && (
        script.textContent.includes('beemi-edit-mode') ||
        script.textContent.includes('edit-selection-indicator') ||
        script.textContent.includes('STORAGE_KEY = \'beemi-edit-mode\'')
      )) {
        script.remove();
      }
    });
    
    console.log('✅ [STRIP] Clean HTML ready (no edit IDs, no scripts)');
    
    // Return the full HTML document
    return doc.documentElement.outerHTML;
  } catch (error) {
    console.error('Error stripping edit attributes:', error);
    return html; // Return original if parsing fails
  }
}

